import { BaseIconProps } from '@casavo/base-ui'
import cn from 'classnames'
import React, { ReactElement, SyntheticEvent } from 'react'

import { button, loadingAnimation, sprinkles } from './styles.css'

export type HeightVariant = 'normal' | 'short'
type StyleVariant = 'primary' | 'secondary' | 'tertiary'
type ShapeVariant = 'round' | 'default'
export type ColorVariant = 'dark' | 'light'
export type SizeVariant = 'small' | 'medium' | 'big'

export interface ButtonCommonProps {
  Icon?: (props: BaseIconProps) => JSX.Element
  ariaLabel?: string
  asLink?: boolean
  center?: boolean
  className?: string
  colorVariant?: ColorVariant
  disabled?: boolean
  fullWidth?: boolean
  iconRight?: boolean
  label?: string | ReactElement
  loading?: boolean
  minWidth?: number
  onClick?: (e: SyntheticEvent) => void
  shapeVariant?: ShapeVariant
  sizeVariant?: SizeVariant
  styleVariant?: StyleVariant
  type?: 'submit' | 'button' | 'reset'
}

type AriaLabelConditionalProps =
  | {
      Icon?: (props: BaseIconProps) => JSX.Element
      ariaLabel: string
      label?: undefined
    }
  | { Icon?: (props: BaseIconProps) => JSX.Element; ariaLabel?: string; label: string | ReactElement }

type MinWidthConditionalProps =
  | { loading?: boolean; minWidth: number }
  | {
      loading?: never
      minWidth?: number
    }

export type ButtonProps = ButtonCommonProps & AriaLabelConditionalProps & MinWidthConditionalProps

export const Button: React.FC<ButtonProps> = ({
  Icon,
  ariaLabel,
  asLink,
  center,
  className,
  colorVariant = 'dark',
  disabled = false,
  fullWidth,
  iconRight,
  label,
  loading = false,
  minWidth,
  onClick,
  shapeVariant = 'default',
  sizeVariant = 'medium',
  styleVariant = 'primary',
  type = 'button',
  ...props
}) => {
  const buttonContent = loading ? (
    <span
      className={cn(
        loadingAnimation
        //colorVariant === 'light' && styleVariant === 'solid' && colorVariants['greyscale400']
      )}
    />
  ) : (
    <>
      {Icon && (
        <Icon
          className={sprinkles({
            flexShrink: 0,
          })}
          width={14}
        />
      )}
      {label && (
        <span
          className={sprinkles({
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          })}
        >
          {label}
        </span>
      )}
    </>
  )

  if (asLink) {
    return (
      <div
        className={cn(
          button({ color: colorVariant, size: sizeVariant, style: styleVariant }),
          iconRight &&
            sprinkles({
              flexDirection: 'row-reverse',
            }),
          disabled &&
            sprinkles({
              cursor: 'not-allowed',
            }),
          center &&
            sprinkles({
              margin: '0 auto',
            }),
          className
        )}
        {...props}
      >
        {buttonContent}
      </div>
    )
  }

  return (
    <button
      aria-label={ariaLabel}
      className={cn(
        button({ color: colorVariant, shape: shapeVariant, size: sizeVariant, style: styleVariant }),
        iconRight &&
          sprinkles({
            flexDirection: 'row-reverse',
          }),
        disabled &&
          sprinkles({
            cursor: 'not-allowed',
          }),
        center &&
          sprinkles({
            margin: '0 auto',
          }),
        fullWidth && sprinkles({ width: '100%' }),
        className
      )}
      disabled={disabled}
      style={minWidth ? { minWidth } : {}}
      type={type}
      onClick={onClick}
      {...props}
    >
      {buttonContent}
    </button>
  )
}
